import { z } from "zod";
import { adminCountSchema, adminDaySchema } from "./common.js";
/**
 * How Crux is used: the Usage page, the Overview's sentence, the customer
 * drawer's section and the feature drawer. Read from `usage_events`, which started filling on the day it
 * shipped — every `since` is that moment, and null means nothing has been
 * recorded yet.
 */
/** `view`: a screen; `action`: a browser write that worked; `call`: an MCP tool call. */
export const adminUsageKindSchema = z.enum(["view", "action", "call"]);
export const adminUsageSourceSchema = z.enum(["web", "mcp"]);
/** An instant, as the server writes it (RFC 3339). */
const instant = z.string().min(1);
/** Which feature: a screen (`view|viewed|fanout`) or one method on one route. */
export const adminUsageFeatureKeySchema = z.object({
    kind: adminUsageKindSchema,
    /** A screen for a view; a route pattern for an action or a call. */
    feature: z.string().min(1).max(200),
    /** `viewed` for a screen; the HTTP method, lower case, for the others. */
    action: z.string().min(1).max(20),
});
/**
 * When "came back" can be told. It compares the 7 days before last with the
 * last 7, so it means nothing until recording is two weeks old: `comparable`
 * is false until `from`.
 */
const comparableShape = {
    since: instant.nullable(),
    comparable: z.boolean(),
    from: instant.nullable(),
};
/** This week against the week before: one meaning of "came back" everywhere. */
const weekShape = {
    /** People in the last 7 days. */
    users: adminCountSchema,
    /** People in the 7 days before those. */
    weekBefore: adminCountSchema,
    /** Of `weekBefore`, the people seen again in the last 7 days. */
    cameBack: adminCountSchema,
};
export const adminUsagePeopleSchema = z.object({
    thisWeek: adminCountSchema,
    weekBefore: adminCountSchema,
    cameBack: adminCountSchema,
    /** First seen at all in the last 7 days. */
    new: adminCountSchema,
});
/** The Overview's one sentence about usage. */
export const adminUsageLineSchema = z.object({
    ...comparableShape,
    people: adminUsagePeopleSchema,
});
/** The Usage page's "Do people come back?". */
export const adminRetentionSchema = z.object({
    since: instant.nullable(),
    /** `weeks[0]` is everyone first seen that week; `weeks[n]`, how many came back n weeks later. */
    cohorts: z.array(z.object({ weekStart: adminDaySchema, weeks: z.array(adminCountSchema) })),
});
/** The Usage page: everyone this week, every page, and the weekly return table. */
export const adminUsageSchema = z.object({
    asOf: instant,
    /** The trend's window, and how far back a page counts as used. */
    days: adminCountSchema,
    ...comparableShape,
    people: adminUsagePeopleSchema,
    daily: z.array(z.object({ day: adminDaySchema, users: adminCountSchema })),
    pages: z.array(z.object({
        ...adminUsageFeatureKeySchema.shape,
        feature: z.string(),
        ...weekShape,
        /** Times used, active time and visits, all in the last 7 days. */
        events: adminCountSchema,
        activeMs: adminCountSchema,
        visits: adminCountSchema,
        lastAt: instant,
        /** People per day over `days`, oldest first; a day nobody came is 0. */
        daily: z.array(adminCountSchema),
    })),
    retention: adminRetentionSchema,
});
/** One square in a person's row: a week before recording began is not known. */
export const adminWeekMarkSchema = z.enum(["used", "not_used", "not_recorded"]);
/** One workspace's own last month: the customer drawer's "How they use Crux". */
export const adminCustomerUsageSchema = z.object({
    since: instant.nullable(),
    days: adminCountSchema,
    totals: z.object({
        people: adminCountSchema,
        visits: adminCountSchema,
        activeMs: adminCountSchema,
        actions: adminCountSchema,
        calls: adminCountSchema,
    }),
    /** The most recent first. */
    people: z.array(z.object({
        userId: z.string(),
        /** Null when the account has since been deleted. */
        name: z.string().nullable(),
        email: z.string().nullable(),
        lastUsedAt: instant,
        /** Changes and Claude calls in the range: neither belongs to a visit. */
        changes: adminCountSchema,
        calls: adminCountSchema,
        /** This week and the seven before, oldest first. */
        weeks: z.array(adminWeekMarkSchema),
        /** Active time per screen in the window, the longest first. */
        screens: z.array(z.object({
            feature: z.string(),
            activeMs: adminCountSchema,
            visits: adminCountSchema,
        })),
        /** Their last visits that were not short, the newest first. */
        visits: z.array(z.object({
            visitId: z.string(),
            userId: z.string(),
            startedAt: instant,
            activeMs: adminCountSchema,
            /** Screens in order; one opened twice running is one step. */
            path: z.array(z.string()),
        })),
        /** Visits under 10 s in all: counted, not listed. */
        shortVisits: adminCountSchema,
    })),
});
/** The feature drawer: one feature's last month, across every workspace. */
export const adminUsageFeatureSchema = z.object({
    asOf: instant,
    kind: adminUsageKindSchema,
    feature: z.string(),
    action: z.string(),
    days: adminCountSchema,
    ...comparableShape,
    totals: z.object({
        users: adminCountSchema,
        events: adminCountSchema,
        workspaces: adminCountSchema,
        activeMs: adminCountSchema,
        /** Visits that used it: what "time per visit" divides by. */
        visits: adminCountSchema,
    }),
    week: z.object(weekShape),
    /**
     * A screen only: the screens opened straight after it in the same visit, and
     * `null` for a visit that ended there. Empty for a change or a call.
     */
    next: z.array(z.object({ next: z.string().nullable(), visits: adminCountSchema })),
    /** Every day of the window; a day nobody used it is a zero. */
    daily: z.array(z.object({ day: adminDaySchema, users: adminCountSchema })),
    workspaces: z.array(z.object({
        workspaceId: z.string(),
        name: z.string(),
        users: adminCountSchema,
        events: adminCountSchema,
    })),
    releases: z.array(z.object({
        release: z.string(),
        firstSeenAt: instant,
        lastSeenAt: instant,
        events: adminCountSchema,
    })),
});
const usageWindowSchema = z.object({
    from: instant,
    to: instant,
    /** False while the `after` window is still running: a partial answer. */
    complete: z.boolean(),
    /** Everyone who used Crux in the window; `users` is a share of it. */
    activeUsers: adminCountSchema,
    users: adminCountSchema,
    events: adminCountSchema,
    activeMs: adminCountSchema,
    returningUsers: adminCountSchema,
});
export const adminUsageCompareQuerySchema = adminUsageFeatureKeySchema.extend({
    day: adminDaySchema,
    days: z.number().int().min(1).max(90),
});
export const adminUsageCompareSchema = z.object({
    kind: adminUsageKindSchema,
    feature: z.string(),
    action: z.string(),
    day: adminDaySchema,
    days: adminCountSchema,
    before: usageWindowSchema,
    after: usageWindowSchema,
});
//# sourceMappingURL=usage.js.map